import { NgClass } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    input,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    signal,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import clone from 'lodash-es/clone';
import noop from 'lodash-es/noop';
import { debounceTime, Subscription } from 'rxjs';
import { ClickOutsideDirective } from 'shared/directives/click-outside.directive';
import { ListItem } from 'shared/interfaces/list-item';
import { HighlightPipe } from 'shared/pipes/highlight.pipe';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';

@Component({
    selector: 'app-typeahead-select',
    templateUrl: './typeahead-select.component.html',
    styleUrls: ['./typeahead-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TypeaheadSelectComponent),
            multi: true,
        },
    ],
    imports: [ClickOutsideDirective, ReactiveFormsModule, NgClass, TranslateModule, HighlightPipe],
})
export class TypeaheadSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
    // Set touched on blur
    @HostListener('blur')
    public onTouched() {
        this.onTouchFunction();
    }

    @ViewChild('selectionContainer', { read: ElementRef }) selectionContainer!: ElementRef;
    @ViewChild('dropdownMenu', { read: ElementRef }) dropdownMenu!: ElementRef;
    @ViewChild('searchInput', { read: ElementRef }) searchInput!: ElementRef;
    @ViewChild('emptyOption', { read: ElementRef }) emptyOption!: ElementRef;
    @ViewChildren('menuItemAuto') optionsChildren!: QueryList<ElementRef>;

    @Output() change: EventEmitter<any> = new EventEmitter<any>();

    @Input() id: string = '';
    @Input() placeholder: string = '';
    emptyOptionLabel = input<string>('');

    disabled = input<boolean>(false);
    disabledByForm = signal<boolean>(false);
    isDisabled = computed(() => this.disabled() || this.disabledByForm());

    @Input()
    public set optionsList(value: any[]) {
        this._optionsList = value;
        this.computeOptions();
    }

    public get optionsList() {
        return this._optionsList;
    }

    @Input()
    public set idKey(value: string) {
        this._idKey = value;
        this.computeOptions();
    }

    public get idKey() {
        return this._idKey;
    }

    @Input()
    public set labelKey(value: string) {
        this._labelKey = value;
        this.computeOptions();
    }

    public get labelKey() {
        return this._labelKey;
    }

    @Input()
    public set showClearSelectionButton(value: boolean) {
        this._showClearSelectionButton = this.required ? false : value;
    }

    public get showClearSelectionButton() {
        return this._showClearSelectionButton;
    }

    @Input()
    public set required(value: boolean | undefined | null) {
        this._required = value !== undefined && value !== null ? value : false;

        if (this._required) {
            this.showClearSelectionButton = false;
        }
    }

    public get required(): boolean {
        return this._required;
    }

    @Input()
    public set showEmptyOption(value: boolean) {
        this._showEmptyOption = value;
        this._lowestIndex = this._showEmptyOption ? -1 : 0;
    }

    public get showEmptyOption() {
        return this._showEmptyOption;
    }

    @Input()
    public set selectFirstAvailableOptionOnReset(value: boolean) {
        this._selectFirstAvailableOptionOnReset = value;
    }

    public get selectFirstAvailableOptionOnReset() {
        return this._selectFirstAvailableOptionOnReset;
    }

    @Input() preserveOriginalOrder: boolean = true;

    defaultValue = input<unknown | null>(null);

    private _optionsList: ListItem[] = [];
    private _idKey: string = '';
    private _labelKey: string = '';
    private _showClearSelectionButton: boolean = true;
    private _showEmptyOption: boolean = false;
    private _selectFirstAvailableOptionOnReset: boolean = false;
    private _required: boolean = false;
    private _internalOptionsList: ListItem[] = [];
    private _lowestIndex: number = 0;
    private _emptyOption = new ListItem({ id: '', label: this.emptyOptionLabel() });
    private onChangeFunction: (_: any) => void = noop;
    private onTouchFunction: () => void = noop;
    private subscriptions: { [key: string]: Subscription | undefined } = {};

    public filteredOptionsList: ListItem[] = [];
    public selectedOption!: ListItem | null;
    public hoveredItemIndex!: number;
    public isOptionsListOpen = signal<boolean>(false);
    public form = new UntypedFormGroup({
        searchInput: new UntypedFormControl(null),
    });

    constructor(
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {
        if (this.required) {
            this.showClearSelectionButton = false;
        }
    }

    public ngOnInit() {
        if (this.showEmptyOption) {
            this._lowestIndex = -1;
        }

        this.subscriptions.valueChanges = this.form
            .get('searchInput')
            ?.valueChanges.pipe(debounceTime(150))
            .subscribe((value: string) => {
                this.isOptionsListOpen.set(value !== undefined);
                this.hoveredItemIndex = value !== '' ? 0 : this._lowestIndex;
                this.filteredOptionsList = (this._internalOptionsList as any[]).filter((option) => {
                    // If the option label is empty, skip the option as we don't need to show empty labels
                    // And this also causes the search to fail with a key is required error
                    if (!option.label) {
                        return false;
                    }
                    const translatedLabel = this.translate.instant(option.label)?.toString();
                    if (translatedLabel?.toLowerCase()?.includes(value?.toLowerCase())) {
                        return option;
                    }
                });
            });
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public registerOnChange(fn: any): void {
        this.onChangeFunction = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouchFunction = fn;
    }

    public setDisabledState(isDisabled: boolean) {
        this.disabledByForm.set(isDisabled);
    }

    public writeValue(value: string): void {
        if (value === undefined || value === null || value === '') {
            this.selectedOption = null;
        } else {
            this.selectedOption = new ListItem({ id: value, label: '' });

            if (!this._internalOptionsList || !this._internalOptionsList.length) {
                this.subscriptions.getOptions?.add(() => {
                    this.handleExternalValueChanges(value);
                });
            } else {
                this.handleExternalValueChanges(value);
            }
        }
    }

    private handleExternalValueChanges(value: string) {
        if (!this._internalOptionsList.length) {
            return;
        }

        const index = this._internalOptionsList.findIndex((item) => item.id.toString() === value.toString());

        if (index !== -1) {
            this.selectOption(this._internalOptionsList[index], false);
        } else if (this.selectFirstAvailableOptionOnReset) {
            this.selectOption(this._internalOptionsList[0], false);
        }
    }

    public selectOption(option?: ListItem, emitEvent = true) {
        if (option) {
            this.selectedOption = this.filteredOptionsList.find((element) => element.id === option.id) ?? this._emptyOption;
        } else {
            if (this.hoveredItemIndex >= this._lowestIndex) {
                this.selectedOption = this.filteredOptionsList[this.hoveredItemIndex] ?? this._emptyOption;
            } else {
                this.selectedOption = this.showEmptyOption ? this._emptyOption : this.filteredOptionsList[0];
            }
        }

        if (emitEvent) {
            this.closeOptionsList();
            this.onChangeFunction(this.selectedOption?.id ?? null);
            this.onTouched();
            this.change.emit(this.selectedOption?.id);
        }
    }

    public mouseMove(index: number) {
        this.hoveredItemIndex = index;
    }

    public toggleOptionsListVisibility(event: Event) {
        if (this.isOptionsListOpen()) {
            this.closeOptionsList(event);
        } else {
            this.openOptionsList(event);
        }
    }

    public openOptionsList(event: Event) {
        event?.preventDefault();
        this.isOptionsListOpen.set(true);
        this.hoveredItemIndex = this.selectedOption?.id
            ? this.filteredOptionsList.findIndex((element) => element.id == this.selectedOption?.id)
            : this._lowestIndex;
        this.cdr.detectChanges();

        this.searchInput?.nativeElement?.focus();

        if (this.selectedOption && !this.isElementFullyVisible()) {
            const currentElement = this.optionsChildren.get(this.hoveredItemIndex)?.nativeElement;
            currentElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }

    public closeOptionsList(event?: Event) {
        event?.stopPropagation();
        event?.preventDefault();

        if (this.isOptionsListOpen()) {
            this.isOptionsListOpen.set(false);
            this.form.get('searchInput')?.setValue(null, { emitEvent: false });
            this.filteredOptionsList = clone(this._internalOptionsList);

            if (!(event instanceof PointerEvent) && this.selectionContainer?.nativeElement) {
                this.selectionContainer.nativeElement.focus();
            }
        }
    }

    public handleClickOutside(event: Event) {
        if (this.form.touched && this.isOptionsListOpen()) {
            this.closeOptionsList(event);
            this.onTouched();
        }
    }

    public clearSelection(event: Event) {
        event?.stopPropagation();
        event?.preventDefault();

        const defaultValue = this.defaultValue();
        if (typeof defaultValue === 'string') {
            // Even though the selectedOption is set as ListItem, the default value of the input is a string
            // So we need to force the type of the selectedOption to work with the default value
            this.selectedOption = defaultValue as unknown as ListItem;
        } else {
            this.selectedOption = null;
        }

        this.onChangeFunction(this.selectedOption);
        this.onTouched();
        this.change.emit(null);
    }

    public moveUp(event: Event) {
        event?.stopPropagation();
        event?.preventDefault();

        if (this.isOptionsListOpen()) {
            this.hoveredItemIndex = Math.max(this.hoveredItemIndex - 1, this._lowestIndex);

            if (!this.isElementFullyVisible()) {
                const currentElement = this.optionsChildren.get(this.hoveredItemIndex)?.nativeElement ?? this.emptyOption.nativeElement;
                currentElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
        } else {
            this.openOptionsList(event);
        }
    }

    public moveDown(event: Event) {
        event?.stopPropagation();
        event?.preventDefault();

        if (this.isOptionsListOpen()) {
            this.hoveredItemIndex = Math.min(this.hoveredItemIndex + 1, this.filteredOptionsList.length - 1);
            if (!this.isElementFullyVisible()) {
                const currentElement = this.optionsChildren.get(this.hoveredItemIndex)?.nativeElement;
                currentElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
        } else {
            this.openOptionsList(event);
        }
    }

    private computeOptions() {
        if (!this.optionsList || !this.idKey || !this.labelKey) {
            this._internalOptionsList = [];
            return;
        }

        const value = this.optionsList;
        this.createOptionsList(value);
    }

    private createOptionsList(data: any[]) {
        this._internalOptionsList = data.map((item: any) =>
            typeof item === 'string' || typeof item === 'number'
                ? new ListItem(item)
                : new ListItem({
                      id: item[this.idKey],
                      label: item[this.labelKey],
                  }),
        );

        if (!this.isOptionsListOpen()) {
            this.filteredOptionsList = this._internalOptionsList.slice();
        }

        // If we previously received a default selected option id in 'writeValue', we saved it
        // Now we search for it in the internalOptionsList and if we find it there, we select it
        if (this.selectedOption?.id) {
            const elementIndex = this._internalOptionsList.findIndex((option) => option.id == this.selectedOption?.id);

            if (elementIndex !== -1) {
                this.selectOption(this._internalOptionsList[elementIndex], false);
            } else {
                if (this.selectFirstAvailableOptionOnReset) {
                    this.selectOption(this._internalOptionsList[0]);
                } else {
                    this.selectOption(this._emptyOption, false);
                }
            }
        } else if (this.selectFirstAvailableOptionOnReset) {
            this.selectOption(this._internalOptionsList[0]);
        } else {
            this.selectOption(this._emptyOption, false);
        }
    }

    public isElementFullyVisible(): boolean {
        //When no more elements are left in the list the checks below will generate a console error
        //since it cannot 'get' the child with index equal to 'selectedIndex'
        if (!this.optionsChildren?.length || this.hoveredItemIndex === -1) {
            return true;
        }

        const currentElement = this.optionsChildren.get(this.hoveredItemIndex)?.nativeElement;
        const elementParent = this.dropdownMenu.nativeElement;

        const elementTop = currentElement.offsetTop;
        const elementBottom = currentElement.offsetTop + currentElement.clientHeight;
        const parentTop = elementParent.scrollTop;
        const parentBottom = elementParent.scrollTop + elementParent.clientHeight;

        return parentTop < elementTop && parentBottom > elementBottom;
    }
}
